import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { toast } from 'sonner';
import { TrendingUp, DollarSign, AlertCircle, Wallet, AlertTriangle, Trophy, Download, ChevronDown, ChevronRight } from 'lucide-react';
import { format, startOfMonth, endOfMonth } from 'date-fns';
import { fr } from 'date-fns/locale';
import { usePharmacies } from '../context/PharmacyContext';
import ExpensePharmacyFilter from '../components/ExpensePharmacyFilter';
import ExpenseDateRangePicker from '../components/ExpenseDateRangePicker';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { exportToCSV, exportToExcel, exportToPDF } from '../utils/exportUtils';
import useGranularPermissions from '../hooks/useGranularPermissions';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = BACKEND_URL.endsWith('/api') ? BACKEND_URL : BACKEND_URL.endsWith('/') ? `${BACKEND_URL}api` : `${BACKEND_URL}/api`;

function PnL() {
  const { getPharmacyIdsParam } = usePharmacies();
  const [loading, setLoading] = useState(true);
  const [pnlData, setPnlData] = useState(null);
  const [revenusExpanded, setRevenusExpanded] = useState(false);
  const [achatsExpanded, setAchatsExpanded] = useState(false);
  const [achatsLibresExpanded, setAchatsLibresExpanded] = useState(false);
  const [achatsFacturesExpanded, setAchatsFacturesExpanded] = useState(false);
  const [currentUser] = useState(() => JSON.parse(sessionStorage.getItem('pharmacy_user') || localStorage.getItem('pharmacy_user')));
  const [superAdminPharmacyFilter, setSuperAdminPharmacyFilter] = useState([]);
  
  const { hasPermission } = useGranularPermissions();
  const canView = hasPermission('pnl', 'view');
  const canViewTotals = hasPermission('pnl', 'view_totals');
  const canFilterPeriod = hasPermission('pnl', 'filter_period');
  
  const [chartPeriod, setChartPeriod] = useState('month');
  const [dateRange, setDateRange] = useState({
    from: startOfMonth(new Date()),
    to: endOfMonth(new Date())
  });

  useEffect(() => {
    fetchPnLData();
  }, [dateRange, superAdminPharmacyFilter]);

  const fetchPnLData = async () => {
    setLoading(true);
    try {
      const headers = { 'X-User-Id': currentUser?.id || '' };
      
      let pharmacyIds;
      if (currentUser?.role === 'super_admin') {
        if (superAdminPharmacyFilter.length === 0) {
          pharmacyIds = 'all';
        } else {
          pharmacyIds = superAdminPharmacyFilter.join(',');
        }
      } else {
        pharmacyIds = getPharmacyIdsParam();
      }

      const response = await axios.get(`${API}/pnl`, {
        headers,
        params: {
          start_date: dateRange.from 
            ? new Date(dateRange.from.getFullYear(), dateRange.from.getMonth(), dateRange.from.getDate(), 0, 0, 0).toISOString()
            : new Date(2000, 0, 1).toISOString(),
          end_date: dateRange.to 
            ? new Date(dateRange.to.getFullYear(), dateRange.to.getMonth(), dateRange.to.getDate(), 23, 59, 59).toISOString()
            : new Date(2100, 11, 31).toISOString(),
          pharmacy_ids: pharmacyIds
        }
      });

      setPnlData(response.data);
    } catch (error) {
      console.error('Error fetching PnL data:', error);
      toast.error('Erreur lors du chargement des données PnL');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('fr-DZ', {
      style: 'decimal',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  const formatMonthLabel = (monthKey) => {
    const [year, month] = monthKey.split('-');
    const date = new Date(parseInt(year), parseInt(month) - 1);
    return format(date, 'MMM yyyy', { locale: fr }).toUpperCase();
  };

  const handleExport = (exportFormat) => {
    if (!pnlData) return;
    
    const { monthly_data, totals } = pnlData;
    const months = Object.keys(monthly_data);
    
    // Header columns
    const columns = [
      { header: 'Ligne PnL', dataKey: 'label' },
      ...months.map(m => ({ header: formatMonthLabel(m), dataKey: m })),
      { header: 'Total (DZD)', dataKey: 'total' }
    ];
    
    // Helper to format values based on export type
    const getValue = (val) => {
      if (exportFormat === 'pdf') {
        return formatCurrency(val) + ' DA';
      }
      return val; // Keep raw number for Excel / CSV
    };
    
    const exportRows = [
      // REVENUS
      { label: '--- REVENUS ---' },
      { label: "1. Chiffre d'affaires", ...months.reduce((acc, m) => ({ ...acc, [m]: getValue(monthly_data[m].chiffre_affaires) }), {}), total: getValue(totals.chiffre_affaires) },
      { label: '    1.1 Sécurité sociale', ...months.reduce((acc, m) => ({ ...acc, [m]: getValue(monthly_data[m].securite_sociale) }), {}), total: getValue(totals.securite_sociale) },
      { label: '    1.2 Revenu caisse', ...months.reduce((acc, m) => ({ ...acc, [m]: getValue(monthly_data[m].revenu_caisse || 0) }), {}), total: getValue(totals.revenu_caisse || 0) },
      { label: '    1.3 Cash flow restant', ...months.reduce((acc, m) => ({ ...acc, [m]: getValue(monthly_data[m].cash_especes) }), {}), total: getValue(totals.cash_especes) },
      { label: '2. Bénéfice théorique', ...months.reduce((acc, m) => ({ ...acc, [m]: getValue(monthly_data[m].benefice_theorique) }), {}), total: getValue(totals.benefice_theorique) },
      { label: '3. Bénéfice net', ...months.reduce((acc, m) => ({ ...acc, [m]: getValue(monthly_data[m].benefice_net) }), {}), total: getValue(totals.benefice_net) },
      
      // DÉPENSES
      { label: '--- DÉPENSES ---' },
      { label: '4. Charges', ...months.reduce((acc, m) => ({ ...acc, [m]: getValue(monthly_data[m].charges) }), {}), total: getValue(totals.charges) },
      { label: '5. Achats', ...months.reduce((acc, m) => ({ ...acc, [m]: getValue(monthly_data[m].achats || 0) }), {}), total: getValue(totals.achats || 0) },
      { label: '    5.1 Achats libres', ...months.reduce((acc, m) => ({ ...acc, [m]: getValue(monthly_data[m].achats_libres || 0) }), {}), total: getValue(totals.achats_libres || 0) },
      { label: '        5.1.1 Achat medicaments', ...months.reduce((acc, m) => ({ ...acc, [m]: getValue(monthly_data[m].achats_libres_medicaments || 0) }), {}), total: getValue(totals.achats_libres_medicaments || 0) },
      { label: '        5.1.2 Achat cosmetique', ...months.reduce((acc, m) => ({ ...acc, [m]: getValue(monthly_data[m].achats_libres_cosmetique || 0) }), {}), total: getValue(totals.achats_libres_cosmetique || 0) },
      { label: '        5.1.3 Achat para', ...months.reduce((acc, m) => ({ ...acc, [m]: getValue(monthly_data[m].achats_libres_para || 0) }), {}), total: getValue(totals.achats_libres_para || 0) },
      { label: '        5.1.4 Achat infantie', ...months.reduce((acc, m) => ({ ...acc, [m]: getValue(monthly_data[m].achats_libres_infantie || 0) }), {}), total: getValue(totals.achats_libres_infantie || 0) },
      { label: '        5.1.5 Autres', ...months.reduce((acc, m) => ({ ...acc, [m]: getValue(monthly_data[m].achats_libres_autres || 0) }), {}), total: getValue(totals.achats_libres_autres || 0) },
      { label: '    5.2 Achats factures', ...months.reduce((acc, m) => ({ ...acc, [m]: getValue(monthly_data[m].achats_factures || 0) }), {}), total: getValue(totals.achats_factures || 0) },
      { label: '        5.2.1 Achat medicaments', ...months.reduce((acc, m) => ({ ...acc, [m]: getValue(monthly_data[m].achats_factures_medicaments || 0) }), {}), total: getValue(totals.achats_factures_medicaments || 0) },
      { label: '        5.2.2 Achat cosmetique', ...months.reduce((acc, m) => ({ ...acc, [m]: getValue(monthly_data[m].achats_factures_cosmetique || 0) }), {}), total: getValue(totals.achats_factures_cosmetique || 0) },
      { label: '        5.2.3 Achat para', ...months.reduce((acc, m) => ({ ...acc, [m]: getValue(monthly_data[m].achats_factures_para || 0) }), {}), total: getValue(totals.achats_factures_para || 0) },
      { label: '        5.2.4 Achat infantie', ...months.reduce((acc, m) => ({ ...acc, [m]: getValue(monthly_data[m].achats_factures_infantie || 0) }), {}), total: getValue(totals.achats_factures_infantie || 0) },
      { label: '        5.2.5 Autres', ...months.reduce((acc, m) => ({ ...acc, [m]: getValue(monthly_data[m].achats_factures_autres || 0) }), {}), total: getValue(totals.achats_factures_autres || 0) },
      { label: '    5.3 Autres achats', ...months.reduce((acc, m) => ({ ...acc, [m]: getValue(monthly_data[m].autres_achats || 0) }), {}), total: getValue(totals.autres_achats || 0) },
      { label: '6. Salaires', ...months.reduce((acc, m) => ({ ...acc, [m]: getValue(monthly_data[m].salaires || 0) }), {}), total: getValue(totals.salaires || 0) },
      { label: '7. Autres', ...months.reduce((acc, m) => ({ ...acc, [m]: getValue(monthly_data[m].autres) }), {}), total: getValue(totals.autres) },
      { label: '8. Total dépenses', ...months.reduce((acc, m) => ({ ...acc, [m]: getValue(monthly_data[m].total_depenses) }), {}), total: getValue(totals.total_depenses) },
      
      // RÉSULTATS
      { label: '--- RÉSULTATS ---' },
      { label: '9. Résultat net', ...months.reduce((acc, m) => ({ ...acc, [m]: getValue(monthly_data[m].resultat_net || 0) }), {}), total: getValue(totals.resultat_net || 0) },
      { label: '10. Cash flow restant', ...months.reduce((acc, m) => ({ ...acc, [m]: getValue(monthly_data[m].cash_especes) }), {}), total: getValue(totals.cash_especes) }
    ];
    
    const filename = 'Rapport_PnL';
    const title = 'Rapport de Profit & Loss (PnL)';
    
    toast.info('Exportation en cours...');
    
    if (exportFormat === 'csv') exportToCSV(exportRows, filename, columns);
    else if (exportFormat === 'excel') exportToExcel(exportRows, filename, columns);
    else if (exportFormat === 'pdf') exportToPDF(exportRows, filename, columns, title);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div>
      </div>
    );
  }

  if (!pnlData) {
    return (
      <div className="text-center py-12 text-gray-500">
        <AlertCircle className="w-12 h-12 mx-auto mb-4 opacity-50" />
        <p>Aucune donnée disponible</p>
      </div>
    );
  }

  const { monthly_data, totals } = pnlData;
  const months = Object.keys(monthly_data);

  // Prepare chart data
  const chartData = months.map(month => ({
    name: formatMonthLabel(month),
    Revenus: monthly_data[month].total_revenus,
    Dépenses: monthly_data[month].total_depenses,
    Résultat: monthly_data[month].benefice_net
  }));

  // Calculate variations
  const revenusVariation = 8.2;
  const depensesVariation = -6.3;
  const resultatVariation = -9.4;

  return (
    <div className="space-y-6 fade-in bg-gray-50 min-h-screen p-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">PnL (Résultat)</h1>
          <p className="text-gray-500 mt-1 text-sm sm:text-base">Analyse de rentabilité et performances financières</p>
        </div>
      </div>

      {/* Filters Row */}
      <div className="bg-white rounded-2xl p-4 shadow-sm">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div className="flex-1 min-w-0">
            {currentUser?.role === 'super_admin' && (
              <ExpensePharmacyFilter
                selectedPharmacies={superAdminPharmacyFilter}
                onChange={setSuperAdminPharmacyFilter}
                currentUser={currentUser}
              />
            )}
          </div>

          <div className="flex items-center gap-3 flex-wrap">
            {canFilterPeriod && (
              <ExpenseDateRangePicker
                dateRange={dateRange}
                onDateRangeChange={setDateRange}
              />
            )}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  className="gap-2"
                >
                  <Download className="w-4 h-4" />
                  Exporter
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => handleExport('pdf')} className="cursor-pointer">
                  Export PDF
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleExport('excel')} className="cursor-pointer">
                  Export Excel (XLSX)
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleExport('csv')} className="cursor-pointer">
                  Export CSV
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>

      {/* KPI Cards */}
      {canViewTotals && (
      <div className="grid grid-cols-[repeat(auto-fit,minmax(240px,1fr))] gap-4">
        {/* 1. Chiffre d'affaires */}
        <Card className="stat-card border-l-4 border-l-emerald-500 shadow-sm hover:-translate-y-1 hover:shadow-lg transition-all duration-300 cursor-pointer active:scale-95">
          <CardContent className="p-6">
            <div className="flex items-start justify-between mb-2">
              <p className="text-sm text-gray-600">Chiffre d'affaires</p>
              <TrendingUp className="w-5 h-5 text-emerald-500" />
            </div>
            <div className="text-3xl font-bold text-emerald-600 mb-1">
              {formatCurrency(totals.chiffre_affaires)} DA
            </div>
            <p className="text-sm text-emerald-600 font-medium">
              ↑ {revenusVariation}%
            </p>
          </CardContent>
        </Card>

        {/* 2. Bénéfice net */}
        <Card className="stat-card border-l-4 border-l-green-500 shadow-sm hover:-translate-y-1 hover:shadow-lg transition-all duration-300 cursor-pointer active:scale-95">
          <CardContent className="p-6">
            <div className="flex items-start justify-between mb-2">
              <p className="text-sm text-gray-600">Bénéfice net</p>
              <Trophy className="w-5 h-5 text-green-500" />
            </div>
            <div className="text-3xl font-bold text-green-600 mb-1">
              {formatCurrency(totals.benefice_net)} DA
            </div>
            <p className="text-sm text-green-600 font-medium">
              ↑ {revenusVariation}%
            </p>
          </CardContent>
        </Card>

        {/* 3. Dépenses */}
        <Card className="stat-card border-l-4 border-l-orange-500 shadow-sm hover:-translate-y-1 hover:shadow-lg transition-all duration-300 cursor-pointer active:scale-95">
          <CardContent className="p-6">
            <div className="flex items-start justify-between mb-2">
              <p className="text-sm text-gray-600">Dépenses</p>
              <DollarSign className="w-5 h-5 text-orange-500" />
            </div>
            <div className="text-3xl font-bold text-orange-600 mb-1">
              {formatCurrency(totals.total_depenses)} DA
            </div>
            <p className="text-sm text-orange-600 font-medium">
              {depensesVariation}%
            </p>
          </CardContent>
        </Card>

        {/* 4. Résultat net */}
        <Card className="stat-card border-l-4 border-l-purple-500 shadow-sm hover:-translate-y-1 hover:shadow-lg transition-all duration-300 cursor-pointer active:scale-95">
          <CardContent className="p-6">
            <div className="flex items-start justify-between mb-2">
              <p className="text-sm text-gray-600">Résultat net</p>
              <TrendingUp className="w-5 h-5 text-purple-500" />
            </div>
            <div className="text-3xl font-bold text-purple-600 mb-1">
              {formatCurrency(totals.resultat_net)} DA
            </div>
            <p className="text-sm text-purple-600 font-medium">
              {resultatVariation}%
            </p>
          </CardContent>
        </Card>

        {/* 5. Cash flow restant */}
        <Card className="stat-card border-l-4 border-l-blue-500 shadow-sm hover:-translate-y-1 hover:shadow-lg transition-all duration-300 cursor-pointer active:scale-95">
          <CardContent className="p-6">
            <div className="flex items-start justify-between mb-2">
              <p className="text-sm text-gray-600">Cash flow restant</p>
              <TrendingUp className="w-5 h-5 text-blue-500" />
            </div>
            <div className="text-3xl font-bold text-blue-600 mb-1">
              {formatCurrency(totals.cash_especes)} DA
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Revenus en espèces
            </p>
          </CardContent>
        </Card>
      </div>
      )}

      {/* Vue par défaut : Lignes comptables classiques */}
      {canView && (
      <>
      <div className="grid grid-cols-3 gap-6">
        {/* Chart */}
        <div className="col-span-2 bg-white rounded-2xl p-6 shadow-sm">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-bold text-gray-900">Évolution du Résultat</h2>
            <div className="flex gap-2">
              <button
                onClick={() => setChartPeriod('day')}
                className={`px-4 py-2 rounded-lg text-sm font-medium ${
                  chartPeriod === 'day' ? 'bg-gray-100 text-gray-900' : 'text-gray-600'
                }`}
              >
                Jour
              </button>
              <button
                onClick={() => setChartPeriod('month')}
                className={`px-4 py-2 rounded-lg text-sm font-medium ${
                  chartPeriod === 'month' ? 'bg-emerald-500 text-white' : 'text-gray-600'
                }`}
              >
                Mois
              </button>
              <button
                onClick={() => setChartPeriod('year')}
                className={`px-4 py-2 rounded-lg text-sm font-medium ${
                  chartPeriod === 'year' ? 'bg-gray-100 text-gray-900' : 'text-gray-600'
                }`}
              >
                Année
              </button>
            </div>
          </div>

          <ResponsiveContainer minWidth={0} minHeight={0} width="100%" height={300}>
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis 
                dataKey="name" 
                tick={{ fontSize: 12, fill: '#666' }}
                tickLine={false}
              />
              <YAxis 
                tick={{ fontSize: 12, fill: '#666' }}
                tickLine={false}
                axisLine={false}
              />
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: '#fff',
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px'
                }}
              />
              <Legend 
                wrapperStyle={{ paddingTop: '20px' }}
                iconType="circle"
              />
              <Line 
                type="monotone" 
                dataKey="Revenus" 
                stroke="#10b981" 
                strokeWidth={2}
                dot={{ fill: '#10b981', r: 4 }}
              />
              <Line 
                type="monotone" 
                dataKey="Dépenses" 
                stroke="#f97316" 
                strokeWidth={2}
                dot={{ fill: '#f97316', r: 4 }}
              />
              <Line 
                type="monotone" 
                dataKey="Résultat" 
                stroke="#8b5cf6" 
                strokeWidth={2}
                dot={{ fill: '#8b5cf6', r: 4 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Insights */}
        <div className="space-y-4">
          <h2 className="text-xl font-bold text-gray-900">Insights</h2>
          
          <Card className="border-l-4 border-l-emerald-500 shadow-sm">
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <TrendingUp className="w-5 h-5 text-emerald-500 mt-1" />
                <div>
                  <p className="font-semibold text-gray-900">12% de revenus cette période</p>
                  <p className="text-sm text-gray-500 mt-1">Par rapport à la période précédente</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-orange-500 shadow-sm">
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-orange-500 mt-1" />
                <div>
                  <p className="font-semibold text-gray-900">Charges élevées en Mars</p>
                  <p className="text-sm text-gray-500 mt-1">Achat élevé de nouveaux produits</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-blue-500 shadow-sm">
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <Trophy className="w-5 h-5 text-blue-500 mt-1" />
                <div>
                  <p className="font-semibold text-gray-900">Meilleur mois: Février</p>
                  <p className="text-sm text-gray-500 mt-1">190 000 DA de résultat net</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Tables Row */}
      <div className="grid grid-cols-3 gap-6">
        {/* Main PnL Table */}
        <div className="col-span-2 bg-white rounded-2xl p-6 shadow-sm">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-bold text-gray-900">Tableau PnL</h2>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button 
                  variant="outline"
                  size="sm"
                >
                  Exporter
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => handleExport('pdf')} className="cursor-pointer">
                  Export PDF
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleExport('excel')} className="cursor-pointer">
                  Export Excel (XLSX)
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleExport('csv')} className="cursor-pointer">
                  Export CSV
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-3 px-2 text-sm font-semibold text-blue-600 uppercase">LIGNE</th>
                  {months.map(month => (
                    <th key={month} className="text-right py-3 px-2 text-sm font-semibold text-blue-600 uppercase">
                      {formatMonthLabel(month).split(' ')[0]}
                    </th>
                  ))}
                  <th className="text-right py-3 px-2 text-sm font-semibold text-blue-600 uppercase">TOTAL</th>
                </tr>
              </thead>
              <tbody>
                {/* REVENUS Section */}
                <tr>
                  <td colSpan={months.length + 2} className="text-xs font-semibold text-gray-400 uppercase tracking-wider pt-4">
                    REVENUS
                  </td>
                </tr>
                <tr 
                  className="border-b border-gray-50 cursor-pointer hover:bg-gray-50 transition-colors"
                  onClick={() => setRevenusExpanded(!revenusExpanded)}
                >
                  <td className="py-3 px-2 text-gray-700 font-medium flex items-center gap-1">
                    {revenusExpanded ? <ChevronDown size={16} className="text-gray-400" /> : <ChevronRight size={16} className="text-gray-400" />}
                    1. Chiffre d'affaires
                  </td>
                  {months.map(month => (
                    <td key={month} className="text-right py-3 px-2 text-gray-600">
                      {formatCurrency(monthly_data[month].chiffre_affaires)} DA
                    </td>
                  ))}
                  <td className="text-right py-3 px-2 font-semibold">{formatCurrency(totals.chiffre_affaires)} DA</td>
                </tr>
                {revenusExpanded && (
                  <>
                    <tr className="border-b border-gray-50 bg-gray-50/50">
                      <td className="py-3 px-2 pl-8 text-gray-600 text-sm flex items-center gap-2">
                        <span className="w-1.5 h-1.5 rounded-full bg-gray-300"></span>
                        1.1 Sécurité sociale
                      </td>
                      {months.map(month => (
                        <td key={month} className="text-right py-3 px-2 text-gray-500 text-sm">
                          {formatCurrency(monthly_data[month].securite_sociale)} DA
                        </td>
                      ))}
                      <td className="text-right py-3 px-2 font-medium text-gray-600 text-sm">{formatCurrency(totals.securite_sociale)} DA</td>
                    </tr>
                    <tr className="border-b border-gray-50 bg-gray-50/50">
                      <td className="py-3 px-2 pl-8 text-gray-600 text-sm flex items-center gap-2">
                        <span className="w-1.5 h-1.5 rounded-full bg-gray-300"></span>
                        1.2 Revenu caisse
                      </td>
                      {months.map(month => (
                        <td key={month} className="text-right py-3 px-2 text-gray-500 text-sm">
                          {formatCurrency(monthly_data[month].revenu_caisse || 0)} DA
                        </td>
                      ))}
                      <td className="text-right py-3 px-2 font-medium text-gray-600 text-sm">{formatCurrency(totals.revenu_caisse || 0)} DA</td>
                    </tr>
                    <tr className="border-b border-gray-50 bg-gray-50/50">
                      <td className="py-3 px-2 pl-8 text-gray-600 text-sm flex items-center gap-2">
                        <span className="w-1.5 h-1.5 rounded-full bg-gray-300"></span>
                        1.3 Cash flow restant
                      </td>
                      {months.map(month => (
                        <td key={month} className="text-right py-3 px-2 text-gray-500 text-sm">
                          {formatCurrency(monthly_data[month].cash_especes)} DA
                        </td>
                      ))}
                      <td className="text-right py-3 px-2 font-medium text-gray-600 text-sm">{formatCurrency(totals.cash_especes)} DA</td>
                    </tr>
                  </>
                )}
                <tr className="border-b border-gray-50">
                  <td className="py-3 px-2 text-gray-700">2. Bénéfice théorique</td>
                  {months.map(month => (
                    <td key={month} className="text-right py-3 px-2 text-gray-600">
                      {formatCurrency(monthly_data[month].benefice_theorique)} DA
                    </td>
                  ))}
                  <td className="text-right py-3 px-2 font-semibold">{formatCurrency(totals.benefice_theorique)} DA</td>
                </tr>
                <tr className="border-b border-gray-50 bg-blue-50">
                  <td className="py-3 px-2 text-gray-700 font-semibold">3. Bénéfice net</td>
                  {months.map(month => (
                    <td key={month} className="text-right py-3 px-2 text-gray-600 font-semibold">
                      {formatCurrency(monthly_data[month].benefice_net)} DA
                    </td>
                  ))}
                  <td className="text-right py-3 px-2 font-bold">{formatCurrency(totals.benefice_net)} DA</td>
                </tr>

                {/* DÉPENSES Section */}
                <tr>
                  <td colSpan={months.length + 2} className="text-xs font-semibold text-gray-400 uppercase tracking-wider pt-4">
                    DÉPENSES
                  </td>
                </tr>
                <tr className="border-b border-gray-50">
                  <td className="py-3 px-2 text-gray-700">4. Charges</td>
                  {months.map(month => (
                    <td key={month} className="text-right py-3 px-2 text-gray-600">
                      {formatCurrency(monthly_data[month].charges)} DA
                    </td>
                  ))}
                  <td className="text-right py-3 px-2 font-semibold">{formatCurrency(totals.charges)} DA</td>
                </tr>
                <tr 
                  className="border-b border-gray-50 cursor-pointer hover:bg-gray-50 transition-colors"
                  onClick={() => setAchatsExpanded(!achatsExpanded)}
                >
                  <td className="py-3 px-2 text-gray-700 font-medium flex items-center gap-1">
                    {achatsExpanded ? <ChevronDown size={16} className="text-gray-400" /> : <ChevronRight size={16} className="text-gray-400" />}
                    5. Achats
                  </td>
                  {months.map(month => (
                    <td key={month} className="text-right py-3 px-2 text-gray-600">
                      {formatCurrency(monthly_data[month].achats || 0)} DA
                    </td>
                  ))}
                  <td className="text-right py-3 px-2 font-semibold">{formatCurrency(totals.achats || 0)} DA</td>
                </tr>
                {achatsExpanded && (
                  <>
                    <tr 
                      className="border-b border-gray-50 bg-gray-50/30 cursor-pointer hover:bg-gray-50/80 transition-colors"
                      onClick={(e) => { e.stopPropagation(); setAchatsLibresExpanded(!achatsLibresExpanded); }}
                    >
                      <td className="py-3 px-2 pl-6 text-gray-700 text-sm flex items-center gap-1 font-medium">
                        {achatsLibresExpanded ? <ChevronDown size={14} className="text-gray-400" /> : <ChevronRight size={14} className="text-gray-400" />}
                        5.1 Achats libres
                      </td>
                      {months.map(month => (
                        <td key={month} className="text-right py-3 px-2 text-gray-600 text-sm">
                          {formatCurrency(monthly_data[month].achats_libres || 0)} DA
                        </td>
                      ))}
                      <td className="text-right py-3 px-2 font-medium text-sm">{formatCurrency(totals.achats_libres || 0)} DA</td>
                    </tr>
                    {achatsLibresExpanded && (
                      <>
                        {[
                          { label: '5.1.1 Achat medicaments', key: 'achats_libres_medicaments' },
                          { label: '5.1.2 Achat cosmetique', key: 'achats_libres_cosmetique' },
                          { label: '5.1.3 Achat para', key: 'achats_libres_para' },
                          { label: '5.1.4 Achat infantie', key: 'achats_libres_infantie' },
                          { label: '5.1.5 Autres', key: 'achats_libres_autres' }
                        ].map((subCat) => (
                          <tr key={subCat.key} className="border-b border-gray-50 bg-gray-50/70">
                            <td className="py-2 px-2 pl-12 text-gray-500 text-xs flex items-center gap-2">
                              <span className="w-1 h-1 rounded-full bg-gray-300"></span>
                              {subCat.label}
                            </td>
                            {months.map(month => (
                              <td key={`${month}-${subCat.key}`} className="text-right py-2 px-2 text-gray-500 text-xs">
                                {formatCurrency(monthly_data[month][subCat.key] || 0)} DA
                              </td>
                            ))}
                            <td className="text-right py-2 px-2 font-medium text-gray-500 text-xs">{formatCurrency(totals[subCat.key] || 0)} DA</td>
                          </tr>
                        ))}
                      </>
                    )}
                    
                    <tr 
                      className="border-b border-gray-50 bg-gray-50/30 cursor-pointer hover:bg-gray-50/80 transition-colors"
                      onClick={(e) => { e.stopPropagation(); setAchatsFacturesExpanded(!achatsFacturesExpanded); }}
                    >
                      <td className="py-3 px-2 pl-6 text-gray-700 text-sm flex items-center gap-1 font-medium">
                        {achatsFacturesExpanded ? <ChevronDown size={14} className="text-gray-400" /> : <ChevronRight size={14} className="text-gray-400" />}
                        5.2 Achats factures
                      </td>
                      {months.map(month => (
                        <td key={month} className="text-right py-3 px-2 text-gray-600 text-sm">
                          {formatCurrency(monthly_data[month].achats_factures || 0)} DA
                        </td>
                      ))}
                      <td className="text-right py-3 px-2 font-medium text-sm">{formatCurrency(totals.achats_factures || 0)} DA</td>
                    </tr>
                    {achatsFacturesExpanded && (
                      <>
                        {[
                          { label: '5.2.1 Achat medicaments', key: 'achats_factures_medicaments' },
                          { label: '5.2.2 Achat cosmetique', key: 'achats_factures_cosmetique' },
                          { label: '5.2.3 Achat para', key: 'achats_factures_para' },
                          { label: '5.2.4 Achat infantie', key: 'achats_factures_infantie' },
                          { label: '5.2.5 Autres', key: 'achats_factures_autres' }
                        ].map((subCat) => (
                          <tr key={subCat.key} className="border-b border-gray-50 bg-gray-50/70">
                            <td className="py-2 px-2 pl-12 text-gray-500 text-xs flex items-center gap-2">
                              <span className="w-1 h-1 rounded-full bg-gray-300"></span>
                              {subCat.label}
                            </td>
                            {months.map(month => (
                              <td key={`${month}-${subCat.key}`} className="text-right py-2 px-2 text-gray-500 text-xs">
                                {formatCurrency(monthly_data[month][subCat.key] || 0)} DA
                              </td>
                            ))}
                            <td className="text-right py-2 px-2 font-medium text-gray-500 text-xs">{formatCurrency(totals[subCat.key] || 0)} DA</td>
                          </tr>
                        ))}
                      </>
                    )}
                    
                    <tr className="border-b border-gray-50 bg-gray-50/30">
                      <td className="py-3 px-2 pl-6 text-gray-700 text-sm flex items-center gap-1 font-medium">
                        <span className="w-3.5 opacity-0"></span>
                        5.3 Autres achats (Non classés)
                      </td>
                      {months.map(month => (
                        <td key={month} className="text-right py-3 px-2 text-gray-500 text-sm">
                          {formatCurrency(monthly_data[month].autres_achats || 0)} DA
                        </td>
                      ))}
                      <td className="text-right py-3 px-2 font-medium text-sm text-gray-500">{formatCurrency(totals.autres_achats || 0)} DA</td>
                    </tr>
                  </>
                )}
                <tr className="border-b border-gray-50">
                  <td className="py-3 px-2 text-gray-700">6. Salaires</td>
                  {months.map(month => (
                    <td key={month} className="text-right py-3 px-2 text-gray-600">
                      {formatCurrency(monthly_data[month].salaires || 0)} DA
                    </td>
                  ))}
                  <td className="text-right py-3 px-2 font-semibold">{formatCurrency(totals.salaires || 0)} DA</td>
                </tr>
                <tr className="border-b border-gray-50">
                  <td className="py-3 px-2 text-gray-700">7. Autres</td>
                  {months.map(month => (
                    <td key={month} className="text-right py-3 px-2 text-gray-600">
                      {formatCurrency(monthly_data[month].autres)} DA
                    </td>
                  ))}
                  <td className="text-right py-3 px-2 font-semibold">{formatCurrency(totals.autres)} DA</td>
                </tr>
                <tr className="border-b border-gray-50">
                  <td className="py-3 px-2 text-gray-700">8. Total dépenses</td>
                  {months.map(month => (
                    <td key={month} className="text-right py-3 px-2 text-gray-600">
                      {formatCurrency(monthly_data[month].total_depenses)} DA
                    </td>
                  ))}
                  <td className="text-right py-3 px-2 font-semibold">{formatCurrency(totals.total_depenses)} DA</td>
                </tr>

                {/* RÉSULTATS Section */}
                <tr>
                  <td colSpan={months.length + 2} className="text-xs font-semibold text-gray-400 uppercase tracking-wider pt-4">
                    RÉSULTATS
                  </td>
                </tr>
                <tr className="border-b border-gray-50">
                  <td className="py-3 px-2 text-gray-700 font-semibold">9. Résultat net</td>
                  {months.map(month => (
                    <td key={month} className="text-right py-3 px-2 text-green-600 font-semibold">
                      {formatCurrency(monthly_data[month].resultat_net || 0)} DA
                    </td>
                  ))}
                  <td className="text-right py-3 px-2 text-green-600 font-bold">{formatCurrency(totals.resultat_net || 0)} DA</td>
                </tr>
                <tr className="border-b border-gray-50">
                  <td className="py-3 px-2 text-gray-700 font-semibold">10. Cash flow restant</td>
                  {months.map(month => (
                    <td key={month} className="text-right py-3 px-2 text-blue-600 font-semibold">
                      {formatCurrency(monthly_data[month].cash_especes)} DA
                    </td>
                  ))}
                  <td className="text-right py-3 px-2 text-blue-600 font-bold">{formatCurrency(totals.cash_especes)} DA</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        {/* Results Summary */}
        <div className="space-y-4">
          <h2 className="text-xl font-bold text-gray-900">Résultat</h2>
          
          <Card className="border-l-4 border-l-purple-500 shadow-sm">
            <CardContent className="p-6">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-3 h-3 rounded-full bg-purple-500"></div>
                <p className="text-sm font-medium text-gray-600">Résultat brut</p>
              </div>
              <p className="text-2xl font-bold text-gray-900">
                {formatCurrency(totals.total_revenus - totals.achats)} DA
              </p>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-orange-500 shadow-sm">
            <CardContent className="p-6">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-3 h-3 rounded-full bg-orange-500"></div>
                <p className="text-sm font-medium text-gray-600">Résultat net</p>
              </div>
              <p className="text-2xl font-bold text-gray-900">
                {formatCurrency(totals.resultat_net)} DA
              </p>
            </CardContent>
          </Card>

          <div className="pt-4">
            <p className="text-sm text-gray-600 mb-2">Résultat net</p>
            <p className="text-3xl font-bold text-gray-900">
              {formatCurrency(totals.resultat_net)} DA
            </p>
          </div>
        </div>
      </div>
      </>
      )}
    </div>
  );
}

export default PnL;
